import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { migrateLocalStorageToSupabase, clearLocalStorageData } from '../utils/migrateToSupabase';
import { Database, ArrowRight, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

export default function Migrate() {
  const { user } = useAuth();
  const [status, setStatus] = useState('idle'); // idle, migrating, success, error
  const [message, setMessage] = useState('');
  const [details, setDetails] = useState(null);

  const handleMigrate = async () => {
    setStatus('migrating');
    setMessage('Migracja w toku...');

    const result = await migrateLocalStorageToSupabase();

    if (result.success) {
      setStatus('success');
      setMessage(result.message);
      setDetails(result);

      // Clear localStorage after successful migration
      if (user) {
        clearLocalStorageData(user.id);
      }
    } else {
      setStatus('error');
      setMessage(result.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-8">
        <div className="flex items-center gap-3 mb-6">
          <Database className="w-8 h-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">
            Migracja do Supabase
          </h1>
        </div>

        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            Ta strona pomoże Ci przenieść dane z localStorage do bazy danych Supabase.
            Dzięki temu Twoje dane będą:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li>Bezpiecznie przechowywane w chmurze</li>
            <li>Dostępne z różnych urządzeń</li>
            <li>Izolowane między użytkownikami</li>
            <li>Chronione przez Row Level Security (RLS)</li>
          </ul>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <strong>Uwaga:</strong> Ta operacja jest jednorazowa. Po migracji dane z localStorage zostaną usunięte.
            </div>
          </div>
        </div>

        {status === 'idle' && (
          <button
            onClick={handleMigrate}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
          >
            Rozpocznij migrację
            <ArrowRight className="w-5 h-5" />
          </button>
        )}

        {status === 'migrating' && (
          <div className="flex items-center justify-center gap-3 py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-600">{message}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <h3 className="font-semibold text-green-900">Migracja zakończona pomyślnie!</h3>
            </div>
            <p className="text-green-800">{message}</p>
            <a
              href="/"
              className="mt-4 inline-block bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              Przejdź do aplikacji
            </a>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-6 h-6 text-red-600" />
              <h3 className="font-semibold text-red-900">Błąd migracji</h3>
            </div>
            <p className="text-red-800">{message}</p>
            <button
              onClick={() => setStatus('idle')}
              className="mt-4 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Spróbuj ponownie
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
